import { fhirClients } from "src-next/shared/api/fhirClient";
import { buildEhrPosterHttpConfig } from "src-next/shared/api/utils/ehrPosterRequestConfig";
import { httpGet } from "src-next/shared/api/httpClient";

import { questionnaireBuilderService } from "../../services/QuestionnaireBuilderService";
import { toPayloadFromFhirQuestionnaire } from "../../utils/questionnaireQuestionnaireTransformers";
import type { QuestionnairePayload } from "../../types";
import {
  toFhirQuestionnaireResponseForPoster,
  toRespondFormValues,
} from "../transformers/questionnaireRespondTransformers";
import type {
  QuestionnaireRespondLoadResult,
  SubmitQuestionnaireResponseInput,
} from "../types";

const UNKNOWN_STATUS_FALLBACK_ERROR_MESSAGE =
  "Este questionário está em edição e não possui versão publicada ativa para resposta.";

function getQuestionnaireHistoryUrl(questionnaireId: string): string {
  return `/admin/fhir/Questionnaire/${encodeURIComponent(questionnaireId)}/_history`;
}

async function getLatestActiveQuestionnaireFromHistory(
  questionnaireId: string,
): Promise<QuestionnairePayload | null> {
  const historyBundle = await httpGet<fhir5.Bundle>(
    getQuestionnaireHistoryUrl(questionnaireId),
  );

  const activeVersions = (historyBundle.entry ?? [])
    .map((entry) => entry.resource)
    .filter(
      (resource): resource is fhir5.Questionnaire =>
        resource?.resourceType === "Questionnaire" &&
        resource.status === "active",
    )
    .map((resource) => ({
      payload: toPayloadFromFhirQuestionnaire(resource),
      updatedAtMs: Date.parse(resource.meta?.lastUpdated ?? ""),
    }))
    .filter(
      (
        entry,
      ): entry is { payload: QuestionnairePayload; updatedAtMs: number } =>
        entry.payload !== null,
    );

  if (activeVersions.length === 0) return null;

  const byMostRecentUpdate = [...activeVersions].sort(
    (a, b) =>
      (Number.isNaN(b.updatedAtMs) ? 0 : b.updatedAtMs) -
      (Number.isNaN(a.updatedAtMs) ? 0 : a.updatedAtMs),
  );

  return byMostRecentUpdate[0]?.payload ?? null;
}

async function loadQuestionnaireById(
  questionnaireId: string,
): Promise<QuestionnaireRespondLoadResult | null> {
  const questionnaire = await questionnaireBuilderService.getById(questionnaireId);
  if (!questionnaire) return null;

  const questionnaireForResponse =
    questionnaire.status === "unknown"
      ? await getLatestActiveQuestionnaireFromHistory(questionnaireId)
      : questionnaire;

  if (!questionnaireForResponse) {
    throw new Error(UNKNOWN_STATUS_FALLBACK_ERROR_MESSAGE);
  }

  return {
    questionnaire: questionnaireForResponse,
    formValues: toRespondFormValues(questionnaireForResponse.questions),
  };
}

/**
 * POST `QuestionnaireResponse` no ehr-runner via `fhirClients.ehrPoster`.
 * Paridade com `PostQuestionnaireResponse.tsx`: URL base + resource, headers FHIR,
 * `x-user-token`, `subject-id`, `organization-id` e `withCredentials: false` quando cross-origin.
 */
async function submitQuestionnaireResponse(
  input: SubmitQuestionnaireResponseInput,
): Promise<fhir5.QuestionnaireResponse> {
  const resource = toFhirQuestionnaireResponseForPoster({
    questionnaireId: input.questionnaireId,
    subjectReference: input.subjectReference,
    questions: input.questionnaire.questions,
    values: input.values,
    usageType: input.questionnaire.usageType,
    questionnaireTitle: input.questionnaire.title,
    appointmentReference: input.appointmentReference,
    authorReference: input.authorReference,
  });

  const client = fhirClients.ehrPoster.resource<fhir5.QuestionnaireResponse>(
    "QuestionnaireResponse",
  );

  const created = await client.create(resource, buildEhrPosterHttpConfig());

  if (!created.id) {
    throw new Error("Resposta do servidor sem id do QuestionnaireResponse.");
  }

  return created;
}

export const questionnaireRespondService = {
  loadQuestionnaireById,
  submitQuestionnaireResponse,
};
